import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

const base =
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none hover:shadow";
const disabledCls = "opacity-50 pointer-events-none cursor-not-allowed";
const enabledCls = "cursor-pointer";

const buttonVariants = cva(`${base}`, {
  variants: {
    variant: {
      default:
        "bg-primary text-primary-foreground hover:bg-primary-hover shadow-shadow-primary",
      destructive:
        "bg-destructive-background text-destructive hover:bg-destructive/20",
      outline: "border border-primary text-primary hover:bg-secondary-hover",
      secondary: "bg-secondary text-secondary-foreground shadow-shadow-primary",
      accent: "bg-accent text-accent-foreground shadow-shadow-primary",
      ghost: "text-primary hover:bg-primary/10",
      link: "text-primary underline-offset-4 hover:underline hover:shadow-none",
    },
    size: {
      default: "h-10 px-4 py-2",
      sm: "h-8 rounded-sm px-3 text-xs",
      lg: "h-11 px-8",
      icon: "h-12 w-12 md:h-10 md:w-10",
    },
  },
  defaultVariants: { variant: "default", size: "default" },
});

type ButtonProps = React.ComponentProps<typeof motion.button> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, disabled, ...props }, ref) => (
    <motion.button
      ref={ref}
      disabled={disabled}
      initial={{ scale: 0.9 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.9 }}
      transition={{ duration: 0.2 }}
      whileTap={disabled ? undefined : { scale: 0.9 }}
      className={cn(
        buttonVariants({ variant, size }),
        disabled ? disabledCls : enabledCls,
        className
      )}
      {...props}
    />
  )
);

Button.displayName = "Button";

export { buttonVariants, Button };
